/**
 * StopQuoteReminders — Phase Q3 customer-facing "Stop reminders about
 * this quote" page. Reached from the secondary link in Q3 recovery
 * emails: /order-missing/quote/:public_id/stop-reminders?t=TOKEN
 *
 * Reuses the Q2 secure resume token; server verifies + suppresses
 * ONLY the automated recovery emails for THIS quote. Transactional
 * order emails, account emails, and the site-wide newsletter are
 * unaffected.
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StopQuoteReminders() {
  const { public_id } = useParams();
  const [search] = useSearchParams();
  const token = search.get("t") || "";
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!public_id || !token) { setState({ status: "invalid" }); return; }
      try {
        await axios.post(
          `${API}/orders/quote/${encodeURIComponent(public_id)}/suppress-recovery`,
          { t: token }
        );
        if (alive) setState({ status: "ok" });
      } catch (err) {
        if (!alive) return;
        const st = err?.response?.status;
        setState({ status: st === 410 ? "expired" : st === 401 ? "invalid"
                          : st === 404 ? "not_found" : "error" });
      }
    })();
    return () => { alive = false; };
  }, [public_id, token]);

  if (state.status === "loading") {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center" data-testid="stopq-loading">
        <Loader2 className="animate-spin inline" size={28} />
        <div className="mt-3 text-sm text-[var(--smoke)]">Updating your preferences…</div>
      </main>
    );
  }

  if (state.status === "ok") {
    return (
      <main className="max-w-lg mx-auto px-4 py-12" data-testid="stopq-success">
        <div className="border-2 border-emerald-800 bg-emerald-50 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-emerald-800 shrink-0 mt-0.5" size={22} />
            <div>
              <h1 className="font-display text-2xl">Reminders stopped for this quote.</h1>
              <p className="mt-2 text-sm">
                We won&#39;t send you any more automated reminders about quote{" "}
                <span className="font-mono">{public_id}</span>.
              </p>
              <p className="mt-2 text-xs text-[var(--smoke)]">
                This does <strong>not</strong> unsubscribe you from your order
                receipts, account emails, or the CollectorUp newsletter. Your
                saved quote is still available — you can return anytime.
              </p>
              <div className="mt-5 flex gap-2 flex-wrap">
                <Link
                  to={`/order-missing/quote/${public_id}?t=${encodeURIComponent(token)}`}
                  className="btn-red text-sm no-underline"
                  data-testid="stopq-return-to-quote"
                >
                  Return to my quote
                </Link>
                <Link to="/order-missing" className="btn-ghost text-sm no-underline">
                  Start a new quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const testid =
    state.status === "expired"    ? "stopq-expired"
  : state.status === "invalid"    ? "stopq-invalid"
  : state.status === "not_found"  ? "stopq-not-found"
                                  : "stopq-error";
  const heading =
    state.status === "expired"    ? "Your link has expired."
  : state.status === "invalid"    ? "That link isn't valid."
  : state.status === "not_found"  ? "We couldn't find that quote."
                                  : "Something went wrong.";

  return (
    <main className="max-w-lg mx-auto px-4 py-12" data-testid={testid}>
      <div className="border-2 border-[var(--panini-red)] bg-white rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-[var(--panini-red)] shrink-0 mt-0.5" size={22} />
          <div>
            <h1 className="font-display text-2xl">{heading}</h1>
            <p className="mt-2 text-sm text-[var(--smoke)]">
              Please open the most recent CollectorUp email for this quote, or
              contact us if you keep getting reminders.
            </p>
            <div className="mt-5 flex gap-2 flex-wrap">
              <Link to="/order-missing" className="btn-red text-sm no-underline">
                Start a new quote
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
